<template>
  <div class="config-page">
    <div class="page-head">
      <div>
        <h2>视频存储配置</h2>
        <p>配置本地存储与腾讯云 COS 参数</p>
      </div>
      <el-button size="mini" icon="el-icon-refresh" :loading="storageLoading" @click="loadStorageConfig">刷新</el-button>
    </div>

    <section class="panel">
      <div class="panel-title">
        <span>存储参数</span>
        <el-tag size="mini" :type="storageConfig.cosActive ? 'success' : 'info'">
          当前：{{ storageConfig.cosActive ? '腾讯云 COS' : '本地存储' }}
        </el-tag>
      </div>
      <el-form :model="storageConfig" label-width="108px" size="mini" class="storage-form" v-loading="storageLoading">
        <el-row :gutter="16">
          <el-col :span="6">
            <el-form-item label="存储方式">
              <el-select v-model="storageConfig.storageType" style="width: 100%">
                <el-option label="本地存储" value="LOCAL"></el-option>
                <el-option label="腾讯云 COS" value="COS"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="启用配置">
              <el-switch v-model="storageConfig.enabled"></el-switch>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="协议">
              <el-select v-model="storageConfig.cosProtocol" style="width: 100%">
                <el-option label="https" value="https"></el-option>
                <el-option label="http" value="http"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="路径前缀">
              <el-input v-model.trim="storageConfig.cosPathPrefix" placeholder="videos"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="COS Region">
              <el-input v-model.trim="storageConfig.cosRegion" placeholder="ap-guangzhou"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="COS Bucket">
              <el-input v-model.trim="storageConfig.cosBucket" placeholder="bucket-appid"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="访问域名">
              <el-input v-model.trim="storageConfig.cosCustomDomain" placeholder="可填 CDN 或自定义域名"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="SecretId">
              <el-input
                  v-model.trim="storageConfig.cosSecretIdInput"
                  show-password
                  :placeholder="storageConfig.cosSecretIdConfigured ? '留空则保留当前 SecretId' : '请输入 SecretId'">
              </el-input>
              <span class="key-preview" v-if="storageConfig.cosSecretIdPreview">当前：{{ storageConfig.cosSecretIdPreview }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="SecretKey">
              <el-input
                  v-model.trim="storageConfig.cosSecretKeyInput"
                  show-password
                  :placeholder="storageConfig.cosSecretKeyConfigured ? '留空则保留当前 SecretKey' : '请输入 SecretKey'">
              </el-input>
              <span class="key-preview" v-if="storageConfig.cosSecretKeyPreview">当前：{{ storageConfig.cosSecretKeyPreview }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="备注">
              <el-input v-model.trim="storageConfig.remark" placeholder="可选"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="本地视频目录">
              <el-input v-model.trim="storageConfig.localBasePath"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="本地封面目录">
              <el-input v-model.trim="storageConfig.localCoverPath"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="本地访问前缀">
              <el-input v-model.trim="storageConfig.localAccessUrlPrefix"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <div class="storage-actions">
          <el-button type="primary" size="mini" icon="el-icon-check" :loading="storageSaving" @click="saveStorage()">保存配置</el-button>
          <el-button
              type="text"
              size="mini"
              class="danger-link"
              :disabled="cosSecretClearDisabled"
              :title="cosSecretClearTip"
              @click="clearCosSecretId">
            清空 SecretId
          </el-button>
          <el-button
              type="text"
              size="mini"
              class="danger-link"
              :disabled="cosSecretClearDisabled"
              :title="cosSecretClearTip"
              @click="clearCosSecretKey">
            清空 SecretKey
          </el-button>
        </div>
      </el-form>
    </section>
  </div>
</template>

<script>
import {
  getVideoStorageConfig,
  saveVideoStorageConfig
} from '@/api/aigcVideo'

export default {
  name: 'VideoStorageConfigPage',
  data() {
    return {
      storageLoading: false,
      storageSaving: false,
      storageConfig: this.defaultStorageConfig()
    }
  },
  mounted() {
    this.loadStorageConfig()
  },
  computed: {
    cosSecretClearDisabled() {
      return this.storageConfig.storageType === 'COS' && this.storageConfig.enabled
    },
    cosSecretClearTip() {
      return this.cosSecretClearDisabled ? '启用腾讯云 COS 时不能清空密钥，请先切换本地存储或关闭启用配置' : ''
    }
  },
  methods: {
    defaultStorageConfig() {
      return {
        storageType: 'LOCAL',
        enabled: true,
        localBasePath: '',
        localCoverPath: '',
        localAccessUrlPrefix: '',
        cosActive: false,
        cosSecretIdConfigured: false,
        cosSecretKeyConfigured: false,
        cosSecretIdPreview: '',
        cosSecretKeyPreview: '',
        cosSecretIdInput: '',
        cosSecretKeyInput: '',
        cosRegion: '',
        cosBucket: '',
        cosPathPrefix: 'videos',
        cosCustomDomain: '',
        cosProtocol: 'https',
        remark: ''
      }
    },
    async loadStorageConfig() {
      this.storageLoading = true
      try {
        const response = await getVideoStorageConfig()
        this.storageConfig = Object.assign(this.defaultStorageConfig(), response.data || {}, {
          cosSecretIdInput: '',
          cosSecretKeyInput: ''
        })
      } catch (error) {
        this.$message.error(error.message || '获取存储配置失败')
      } finally {
        this.storageLoading = false
      }
    },
    async saveStorage(clearField) {
      const isClearingSecret = clearField === 'secretId' || clearField === 'secretKey'
      if (isClearingSecret && this.storageConfig.storageType === 'COS' && this.storageConfig.enabled) {
        this.$message.warning('启用腾讯云 COS 时不能清空密钥，请先切换本地存储或关闭启用配置')
        return
      }
      if (this.storageConfig.storageType === 'COS' && this.storageConfig.enabled) {
        if (!this.storageConfig.cosRegion) {
          this.$message.warning('请填写 COS Region')
          return
        }
        if (!this.storageConfig.cosBucket) {
          this.$message.warning('请填写 COS Bucket')
          return
        }
        if (!this.storageConfig.cosSecretIdInput && !this.storageConfig.cosSecretIdConfigured) {
          this.$message.warning('请填写 COS SecretId')
          return
        }
        if (!this.storageConfig.cosSecretKeyInput && !this.storageConfig.cosSecretKeyConfigured) {
          this.$message.warning('请填写 COS SecretKey')
          return
        }
      }
      this.storageSaving = true
      try {
        const response = await saveVideoStorageConfig({
          storageType: this.storageConfig.storageType,
          enabled: this.storageConfig.enabled,
          localBasePath: this.storageConfig.localBasePath,
          localCoverPath: this.storageConfig.localCoverPath,
          localAccessUrlPrefix: this.storageConfig.localAccessUrlPrefix,
          cosSecretId: this.storageConfig.cosSecretIdInput,
          cosSecretKey: this.storageConfig.cosSecretKeyInput,
          cosRegion: this.storageConfig.cosRegion,
          cosBucket: this.storageConfig.cosBucket,
          cosPathPrefix: this.storageConfig.cosPathPrefix,
          cosCustomDomain: this.storageConfig.cosCustomDomain,
          cosProtocol: this.storageConfig.cosProtocol,
          remark: this.storageConfig.remark,
          clearCosSecretId: clearField === 'secretId',
          clearCosSecretKey: clearField === 'secretKey'
        })
        this.storageConfig = Object.assign(this.defaultStorageConfig(), response.data || {}, {
          cosSecretIdInput: '',
          cosSecretKeyInput: ''
        })
        this.$message.success('存储配置已保存')
      } catch (error) {
        this.$message.error(error.message || '保存存储配置失败')
      } finally {
        this.storageSaving = false
      }
    },
    async clearCosSecretId() {
      if (this.cosSecretClearDisabled) {
        this.$message.warning(this.cosSecretClearTip)
        return
      }
      try {
        await this.$confirm('确认清空 COS SecretId？', '清空确认', {
          confirmButtonText: '确认清空',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      await this.saveStorage('secretId')
    },
    async clearCosSecretKey() {
      if (this.cosSecretClearDisabled) {
        this.$message.warning(this.cosSecretClearTip)
        return
      }
      try {
        await this.$confirm('确认清空 COS SecretKey？', '清空确认', {
          confirmButtonText: '确认清空',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      await this.saveStorage('secretKey')
    }
  }
}
</script>

<style scoped>
.config-page {
  min-height: 100%;
  padding: 18px;
  background: #f5f7fb;
  color: #303133;
  box-sizing: border-box;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.page-head h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: 0;
}

.page-head p {
  margin: 4px 0 0;
  color: #606266;
  font-size: 13px;
}

.panel {
  padding: 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.panel-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 700;
}

.storage-form {
  max-width: 1280px;
}

.storage-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 108px;
}

.key-preview {
  display: inline-block;
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.danger-link {
  color: #f56c6c;
}

@media (max-width: 980px) {
  .page-head,
  .panel-title,
  .storage-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .storage-actions {
    padding-left: 0;
  }
}
</style>
